/* eslint-disable no-undef */
/**
 * Utility to sync form control configurations between dashboard and extension storage
 * Uses chrome.runtime.sendMessage to communicate with background script
 */

/**
 * Check if extension context is available
 */
const isExtensionAvailable = () => {
  return typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage;
};

/**
 * Send message to extension background script
 */
const sendMessage = (action, data = {}) => {
  return new Promise((resolve, reject) => {
    if (!isExtensionAvailable()) {
      reject(new Error('Extension context not available'));
      return;
    }

    chrome.runtime.sendMessage(
      { action, data },
      (response) => {
        if (chrome.runtime.lastError) {
          reject(new Error(chrome.runtime.lastError.message));
        } else {
          resolve(response);
        }
      }
    );
  });
};

/**
 * Get form control configurations from extension storage
 */
export const getFormControlConfigs = async () => {
  try {
    const response = await sendMessage('getFormControlConfigs');
    return {
      defaultFields: response?.defaultFields || {},
      inputFieldConfigs: response?.inputFieldConfigs || [],
      radioButtons: response?.radioButtons || [],
      dropdowns: response?.dropdowns || [],
    };
  } catch (error) {
    console.warn('Failed to get form control configs from extension:', error);
    // Fallback to localStorage if extension not available
    try {
      const stored = localStorage.getItem('applyease_form_control_configs');
      return stored ? JSON.parse(stored) : {
        defaultFields: {},
        inputFieldConfigs: [],
        radioButtons: [],
        dropdowns: [],
      };
    } catch {
      return {
        defaultFields: {},
        inputFieldConfigs: [],
        radioButtons: [],
        dropdowns: [],
      };
    }
  }
};

/**
 * Set form control configurations to extension storage
 */
export const setFormControlConfigs = async (configs) => {
  try {
    await sendMessage('setFormControlConfigs', configs);
    // Also store in localStorage as backup
    localStorage.setItem('applyease_form_control_configs', JSON.stringify(configs));
    return { success: true };
  } catch (error) {
    console.warn('Failed to set form control configs to extension:', error);
    // Fallback to localStorage
    try {
      localStorage.setItem('applyease_form_control_configs', JSON.stringify(configs));
      return { success: true, warning: 'Saved to localStorage only (extension not available)' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }
};

/**
 * Sync form control configurations from dashboard to extension
 */
export const syncFormControlConfigs = async (configs) => {
  return setFormControlConfigs(configs);
};

/**
 * Check if extension is available and synced
 */
export const checkExtensionStatus = async () => {
  // First check if chrome runtime is available
  if (!isExtensionAvailable()) {
    return { available: false, synced: false };
  }
  
  try {
    // Try to ping the extension with a timeout
    const response = await Promise.race([
      sendMessage('checkExtensionStatus'),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 2000))
    ]);
    return { available: true, synced: response?.synced || false };
  } catch (error) {
    // If error is not "Extension context not available", try alternative check
    if (error.message && !error.message.includes('Extension context not available')) {
      // Try to check if extension ID exists
      try {
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id) {
          return { available: true, synced: false };
        }
      } catch {}
    }
    return { available: false, synced: false };
  }
};

export const getLearnedFields = async () => {
  try {
    const response = await sendMessage('getLearnedFields');
    const items = Array.isArray(response?.items) ? response.items : [];
    return items;
  } catch (error) {
    // Fallback to localStorage for non-extension contexts
    try {
      const stored = localStorage.getItem('applyease_learned_fields');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }
};

export const clearLearnedFields = async () => {
  try {
    await sendMessage('clearLearnedFields');
    try { localStorage.removeItem('applyease_learned_fields'); } catch {}
    return { success: true };
  } catch (error) {
    try {
      localStorage.removeItem('applyease_learned_fields');
      return { success: true, warning: 'Cleared localStorage only (extension not available)' };
    } catch (e) {
      return { success: false, error: e.message };
    }
  }
};

export const dismissLearnedField = async (fingerprint) => {
  try {
    await sendMessage('dismissLearnedField', { fingerprint });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

